import { PRODUCT_PLACEHOLDERS } from "./product-placeholders";
// ---------------------------------------------------------------------------
// GLUE & ADHESIVE PRODUCTS — mirrored from supplier (Trivantage) "glue" search
// results. Each product has its own internal detail page at
// /shop/glues/$slug so customers stay on our site instead of being redirected.
//
// Source: trivantage.com search "glue" — Item numbers, SKUs, sizes and image
// URLs match the supplier catalog. Prices are intentionally NOT shown here;
// customers add items to their order and we email a written quote.
// ---------------------------------------------------------------------------

export type GlueProduct = {
  id: string;
  slug: string;
  name: string;
  size: string;
  itemNumber: string;
  /** Manufacturer's part / model number, e.g. "TM-1" */
  partNumber?: string;
  /** Short blurb shown on the listing card */
  blurb: string;
  /** Long description shown on the detail page (paragraphs) */
  description: string[];
  /** Bullet-point feature list */
  features: string[];
  /** Spec table key/value pairs */
  specs: { label: string; value: string }[];
  /** Product image URL (hosted by supplier CDN) */
  image: string;
  /** Optional SDS / safety data sheet PDF link */
  sdsUrl?: string;
  /** Optional product note ("Ground shipping only", etc.) */
  productNote?: string;
};

export const glueProducts: GlueProduct[] = [
  // --- Xtreme Seal -----------------------------------------------------------
  {
    id: "xtreme-seal-pod9-black-100ft",
    slug: "xtreme-seal-ultra-seal-pod9-black-100ft",
    name: "Xtreme Seal Ultra Seal",
    size: "Black POD9 — 100 ft Roll",
    itemNumber: "578051",
    partNumber: "POD9",
    blurb:
      "Pre-formed butyl sealing tape used on awnings, RVs, marine, signage and metal roofing — replaces messy tube adhesives.",
    description: [
      "Xtreme Seal Ultra Seal POD9 is a pre-formed, butyl-based sealing tape engineered to bond and waterproof a wide range of materials. Widely used in the awning, RV, marine, signage and metal roofing industries as a clean alternative to tube cements and caulks.",
      "Forms a permanent, flexible, weatherproof seal that won't crack, dry out or shrink. Bonds to vinyl, fabric, aluminum, steel, fiberglass, wood and most plastics — even in cold weather and on damp surfaces.",
      "Sold here as a full 100-foot roll (the only size offered) — ideal for shop use, awning fabricators and large repair jobs.",
    ],
    features: [
      "Pre-formed butyl sealing tape — no mixing, no mess",
      "Permanent, flexible, weatherproof bond",
      "Bonds vinyl, metal, fiberglass, wood, plastic",
      "Works in cold weather and on damp surfaces",
      "Full 100-ft roll — shop / production size",
    ],
    specs: [
      { label: "Product Category", value: "Butyl Sealing Tape" },
      { label: "Color", value: "Black" },
      { label: "Length", value: "100 ft" },
      { label: "Part #", value: "POD9" },
      { label: "Item #", value: "578051" },
      { label: "Manufacturer", value: "Xtreme Seal" },
      { label: "Sold As", value: "Full Rolls Only" },
    ],
    image: PRODUCT_PLACEHOLDERS.adhesive,
    productNote: "Sold in full 100-ft rolls only.",
  },

  // --- Tear Mender (4 sizes) -------------------------------------------------
  {
    id: "tear-mender-tm1-2oz",
    slug: "tear-mender-adhesive-tm1-2oz",
    name: "Tear Mender Adhesive",
    size: "2-oz Bottle #TM-1",
    itemNumber: "212005",
    partNumber: "TM-1",
    blurb:
      "Water-based instant repair adhesive for fabric, canvas, leather and vinyl — no sewing required.",
    description: [
      "Tear Mender is a fast-acting, water-based adhesive that bonds fabric, canvas, leather and vinyl in 3 minutes and is fully cured in 15 minutes. The bond is permanent, flexible and waterproof — and won't darken or stain most fabrics.",
      "Perfect for emergency awning, tent and upholstery repairs in the field. Non-toxic, non-flammable and dries clear.",
      "2-oz bottle (#TM-1) is ideal for tackle boxes, RV kits and at-home repairs.",
    ],
    features: [
      "Bonds in 3 minutes, cures in 15",
      "Permanent, flexible, waterproof bond",
      "Non-toxic and non-flammable",
      "Dries clear, won't stain fabric",
      "No sewing or stitching needed",
    ],
    specs: [
      { label: "Product Category", value: "Fabric & Leather Adhesive" },
      { label: "Put Up", value: "2 oz. bottle" },
      { label: "Part #", value: "TM-1" },
      { label: "Item #", value: "212005" },
      { label: "Manufacturer", value: "Val-A / Tear Mender" },
    ],
    image: PRODUCT_PLACEHOLDERS.adhesive,
  },
  {
    id: "tear-mender-tg6h-6oz",
    slug: "tear-mender-adhesive-tg6h-6oz",
    name: "Tear Mender Adhesive",
    size: "6-oz Bottle #TG-6H",
    itemNumber: "212006",
    partNumber: "TG-6H",
    blurb:
      "Larger 6-oz bottle of the popular instant fabric & leather repair adhesive.",
    description: [
      "Same trusted Tear Mender water-based adhesive in a 6-oz bottle (#TG-6H). Bonds fabric, canvas, leather and vinyl in 3 minutes and is fully cured in 15 minutes.",
      "Permanent, flexible and waterproof bond that won't darken or stain most fabrics. Non-toxic, non-flammable and dries clear.",
      "6-oz size is great for shop use and larger repair projects on awnings, tents, upholstery and outdoor gear.",
    ],
    features: [
      "Larger 6-oz size for shop use",
      "Bonds in 3 minutes, cures in 15",
      "Permanent, flexible, waterproof bond",
      "Non-toxic and non-flammable",
      "Dries clear, won't stain fabric",
    ],
    specs: [
      { label: "Product Category", value: "Fabric & Leather Adhesive" },
      { label: "Put Up", value: "6 oz. bottle" },
      { label: "Part #", value: "TG-6H" },
      { label: "Item #", value: "212006" },
      { label: "Manufacturer", value: "Val-A / Tear Mender" },
    ],
    image: PRODUCT_PLACEHOLDERS.adhesive,
  },
  {
    id: "tear-mender-tg16-16oz",
    slug: "tear-mender-adhesive-tg16-16oz",
    name: "Tear Mender Adhesive",
    size: "16-oz Bottle #TG-16",
    itemNumber: "212007",
    partNumber: "TG-16",
    blurb:
      "16-oz pint bottle of Tear Mender — shop size for awning, upholstery and canvas work.",
    description: [
      "16-oz (pint) bottle of Tear Mender water-based instant adhesive. Bonds fabric, canvas, leather and vinyl in 3 minutes and is fully cured in 15 minutes.",
      "Permanent, flexible and waterproof bond — non-toxic, non-flammable and dries clear. Won't darken or stain most fabrics.",
      "Ideal for canvas shops, upholstery work and high-volume repair operations.",
    ],
    features: [
      "16-oz pint shop size",
      "Bonds in 3 minutes, cures in 15",
      "Permanent, flexible, waterproof bond",
      "Non-toxic and non-flammable",
      "Dries clear, won't stain fabric",
    ],
    specs: [
      { label: "Product Category", value: "Fabric & Leather Adhesive" },
      { label: "Put Up", value: "16 oz. bottle" },
      { label: "Part #", value: "TG-16" },
      { label: "Item #", value: "212007" },
      { label: "Manufacturer", value: "Val-A / Tear Mender" },
    ],
    image: PRODUCT_PLACEHOLDERS.adhesive,
  },
  {
    id: "tear-mender-tg640-5gal",
    slug: "tear-mender-adhesive-tg640-5gal",
    name: "Tear Mender Adhesive",
    size: "5-Gallon Bucket #TG-640",
    itemNumber: "212012",
    partNumber: "TG-640",
    blurb:
      "Bulk 5-gallon bucket of Tear Mender — for production shops and high-volume fabricators.",
    description: [
      "Bulk 5-gallon bucket (#TG-640) of Tear Mender water-based instant adhesive. Designed for production shops, awning fabricators and large-scale upholstery operations.",
      "Same fast-cure, permanent, flexible, waterproof bond as the smaller sizes — bonds fabric, canvas, leather and vinyl in 3 minutes and fully cures in 15.",
      "Non-toxic, non-flammable, dries clear and won't stain most fabrics.",
    ],
    features: [
      "Bulk 5-gallon production size",
      "Bonds in 3 minutes, cures in 15",
      "Permanent, flexible, waterproof bond",
      "Non-toxic and non-flammable",
      "Dries clear, won't stain fabric",
    ],
    specs: [
      { label: "Product Category", value: "Fabric & Leather Adhesive" },
      { label: "Put Up", value: "5 gal. bucket" },
      { label: "Part #", value: "TG-640" },
      { label: "Item #", value: "212012" },
      { label: "Manufacturer", value: "Val-A / Tear Mender" },
    ],
    image: PRODUCT_PLACEHOLDERS.adhesive,
  },

  // --- Sprayway Silicone -----------------------------------------------------
  {
    id: "sprayway-silicone-sw945-11oz",
    slug: "sprayway-silicone-spray-sw945-11oz",
    name: "Sprayway® Silicone Spray",
    size: "11-oz Spray Can #SW945",
    itemNumber: "230110",
    partNumber: "SW945",
    blurb:
      "Multi-purpose silicone spray lubricant — protects, lubricates and waterproofs zippers, snaps, vinyl windows and moving parts.",
    description: [
      "Sprayway® SW945 is a fast-drying, multi-purpose silicone spray that lubricates, waterproofs and protects a wide range of materials. Widely used on awning hardware, RV slide-outs, marine canvas zippers, snaps and vinyl windows.",
      "Leaves a clear, non-staining film that repels water, prevents sticking and silences squeaks. Safe on rubber, vinyl, plastic, wood and most painted surfaces.",
      "11-oz aerosol can — the standard shop size for canvas, marine and awning work.",
    ],
    features: [
      "Lubricates, waterproofs and protects",
      "Clear, non-staining silicone film",
      "Safe on rubber, vinyl, plastic, wood",
      "Frees stuck zippers, snaps and slides",
      "Repels water and prevents sticking",
    ],
    specs: [
      { label: "Product Category", value: "Silicone Spray Lubricant" },
      { label: "Put Up", value: "11 oz. aerosol" },
      { label: "Part #", value: "SW945" },
      { label: "Item #", value: "230110" },
      { label: "Manufacturer", value: "Sprayway" },
    ],
    image: PRODUCT_PLACEHOLDERS.adhesive,
    productNote: "Ground shipping only — aerosol / flammable.",
  },
];

export function getGlueBySlug(slug: string): GlueProduct | undefined {
  return glueProducts.find((p) => p.slug === slug);
}
